import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { CreditCard, RotateCcw, Check, Upload, Paperclip, Link2, ChevronDown, ChevronRight, Scissors, X, Plus, Trash2, FileText } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/dashboard/Header";

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const money = (n, c = "USD") => (n || n === 0 ? new Intl.NumberFormat("en-US", { style: "currency", currency: c || "USD" }).format(n) : "—");
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return iso; } };

function CardMapping({ bondLocations, onChanged }) {
  const [open, setOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/ramp/cards"); setCards(data.cards || []); }
    catch (e) { setCards([]); toast.error("Ramp cards couldn't load", { description: e.userMessage }); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const setLoc = async (card, location) => {
    try {
      await api.put("/ramp/card-map", { card_ids: card.card_ids, location });
      toast.success(location ? `${card.cardholder_name} → ${location}` : "Default cleared");
      load(); onChanged && onChanged();
    } catch (err) { toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const applySuggested = async () => {
    const todo = cards.filter((c) => !c.location && c.suggested_location);
    for (const c of todo) await api.put("/ramp/card-map", { card_ids: c.card_ids, location: c.suggested_location });
    toast.success(`Mapped ${todo.length} card${todo.length === 1 ? "" : "s"} by name`);
    load(); onChanged && onChanged();
  };
  return (
    <div className="bg-brand-surface border border-brand-border rounded-md" data-testid="ramp-card-mapping">
      <button onClick={() => setOpen((v) => !v)} data-testid="ramp-cardmap-toggle"
        className="w-full flex items-center justify-between px-5 py-3 text-sm text-brand-ink">
        <span className="flex items-center gap-2"><Link2 className="h-4 w-4 text-brand-tertiary" /> Card → location mapping <span className="text-brand-tertiary text-xs">(admin)</span></span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open && (
        <div className="px-5 pb-4">
          <div className="flex justify-between items-center mb-3">
            <p className="text-[11px] text-brand-muted max-w-lg">Optional default only. Each charge's location is set per-expense below (and syncs to Ramp). Map location cards (e.g. "Bond Collective 55 Broadway"); leave corporate people and shared cards unmapped.</p>
            <button onClick={applySuggested} data-testid="ramp-apply-suggested" className="text-xs bg-brand-primary text-brand-page px-3 py-1.5 rounded shrink-0">Apply name matches</button>
          </div>
          {loading ? <p className="text-sm text-brand-tertiary py-4">Loading…</p> : (
            <div className="divide-y divide-brand-border/60 max-h-[340px] overflow-auto">
              {cards.map((c) => (
                <div key={c.cardholder_name} className="flex items-center gap-3 py-2" data-testid={`ramp-card-${c.card_ids[0]}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-brand-ink truncate">{c.cardholder_name} <span className="text-brand-tertiary text-xs">·{(c.last_fours || []).join(" ·")}</span></p>
                    {!c.location && c.suggested_location && <p className="text-[10px] text-brand-gold">suggested: {c.suggested_location}</p>}
                  </div>
                  <select value={c.location || ""} onChange={(e) => setLoc(c, e.target.value)} data-testid={`ramp-card-select-${c.card_ids[0]}`}
                    className="border border-brand-border rounded px-2 py-1 text-xs bg-brand-page text-brand-ink max-w-[180px]">
                    <option value="">— unmapped —</option>
                    {bondLocations.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ tx, meta, onWrite, onPatch, onReceipt, onSplit }) {
  const [gl, setGl] = useState(tx.gl_category || "");
  const [loc, setLoc] = useState(tx.ramp_location || "");
  const [memo, setMemo] = useState(tx.memo || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const glOptByName = useMemo(() => Object.fromEntries((meta.gl_accounts || []).map((g) => [g.name, g.option_id])), [meta]);
  const locOptByName = useMemo(() => Object.fromEntries((meta.location_options || []).map((o) => [o.value, o.option_id])), [meta]);
  const dirty = gl !== (tx.gl_category || "") || loc !== (tx.ramp_location || "") || (memo || "") !== (tx.memo || "");

  const save = async () => {
    setBusy(true);
    try {
      const coding = {};
      if (gl !== (tx.gl_category || "") && glOptByName[gl]) { coding.gl_field_id = meta.gl_field_id; coding.gl_option_id = glOptByName[gl]; }
      if (loc !== (tx.ramp_location || "") && locOptByName[loc]) { coding.location_field_id = meta.location_field_id; coding.location_option_id = locOptByName[loc]; }
      if (coding.gl_option_id || coding.location_option_id) await api.post(`/ramp/transactions/${tx.id}/coding`, coding);
      if ((memo || "") !== (tx.memo || "")) await api.post(`/ramp/transactions/${tx.id}/memo`, { memo });
      toast.success("Saved to Ramp");
      onPatch(tx.id, { gl_category: gl, ramp_location: loc, memo, location: loc || tx.card_default_location });
    } catch (err) { toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setBusy(false); }
  };
  const markReviewed = async () => {
    setBusy(true);
    try { await api.post(`/ramp/transactions/${tx.id}/ready`); toast.success("Marked ready to sync"); onPatch(tx.id, { reviewed: true }); }
    catch (err) { toast.error("Failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); } finally { setBusy(false); }
  };
  const uploadReceipt = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!tx.user_id) { toast.error("No cardholder on this charge"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", f); fd.append("user_id", tx.user_id);
    try { await api.post(`/ramp/transactions/${tx.id}/receipt`, fd); toast.success("Receipt uploaded to Ramp"); onWrite(); }
    catch (err) { toast.error("Upload failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <tr className={`border-b border-brand-border/60 ${busy ? "opacity-50" : ""}`} data-testid={`ramp-tx-${tx.id}`}>
      <td className="py-2 pr-2 text-brand-muted whitespace-nowrap">{fmtDate(tx.date)}</td>
      <td className="py-2 pr-2">
        <p className="text-brand-ink font-medium truncate max-w-[180px]">{tx.merchant || "—"}</p>
        <p className="text-[10px] text-brand-tertiary truncate max-w-[180px]">{tx.cardholder}{tx.location ? ` · ${tx.location}` : ""}</p>
      </td>
      <td className="py-2 pr-2 text-right font-mono text-brand-ink whitespace-nowrap">
        {money(tx.amount, tx.currency)}
        <button onClick={() => onSplit(tx)} data-testid={`ramp-split-${tx.id}`} title="Split this charge across categories/locations"
          className="mt-0.5 flex items-center gap-1 ml-auto text-[10px] text-brand-muted hover:text-brand-primary"><Scissors className="h-3 w-3" /> Split</button>
      </td>
      <td className="py-2 pr-2">
        <select value={gl} onChange={(e) => setGl(e.target.value)} data-testid={`ramp-gl-${tx.id}`}
          className="border border-brand-border rounded px-2 py-1 text-xs bg-brand-page text-brand-ink max-w-[170px] w-full">
          <option value="">— category —</option>
          {(meta.gl_accounts || []).map((g) => <option key={g.option_id} value={g.name}>{g.name}</option>)}
        </select>
      </td>
      <td className="py-2 pr-2">
        <select value={loc} onChange={(e) => setLoc(e.target.value)} data-testid={`ramp-loc-${tx.id}`}
          className="border border-brand-border rounded px-2 py-1 text-xs bg-brand-page text-brand-ink max-w-[130px] w-full">
          <option value="">— location —</option>
          {(meta.location_options || []).map((o) => <option key={o.option_id} value={o.value}>{o.value}</option>)}
        </select>
      </td>
      <td className="py-2 pr-2">
        <input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="memo…" data-testid={`ramp-memo-${tx.id}`}
          className="border border-brand-border rounded px-2 py-1 text-xs bg-brand-page text-brand-ink w-[130px]" />
      </td>
      <td className="py-2 pr-2 text-center">
        <input ref={fileRef} type="file" className="hidden" onChange={uploadReceipt} data-testid={`ramp-receipt-input-${tx.id}`} accept="image/*,application/pdf" />
        {tx.has_receipt ? (
          <button onClick={() => onReceipt(tx)} data-testid={`ramp-receipt-${tx.id}`} title="View receipt"
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-brand-pos/40 text-brand-pos hover:bg-brand-pos/10">
            <Paperclip className="h-3 w-3" /> View
          </button>
        ) : (
          <button onClick={() => fileRef.current?.click()} data-testid={`ramp-receipt-${tx.id}`} title="Upload receipt"
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-brand-border text-brand-muted hover:text-brand-ink">
            <Upload className="h-3 w-3" /> Add
          </button>
        )}
      </td>
      <td className="py-2 text-center">
        <div className="flex flex-col items-center gap-1">
          <button onClick={save} disabled={!dirty || busy} data-testid={`ramp-save-${tx.id}`}
            className="text-[11px] bg-brand-primary text-brand-page px-2 py-1 rounded hover:bg-brand-primaryHover disabled:opacity-30 w-full">
            {busy ? "Saving…" : dirty ? "Save" : "Saved"}
          </button>
          {tx.reviewed
            ? <span className="inline-flex items-center gap-1 text-[11px] text-brand-pos" data-testid={`ramp-reviewed-${tx.id}`}><Check className="h-3 w-3" /> Ready to sync</span>
            : <button onClick={markReviewed} disabled={busy || dirty} title={dirty ? "Save your changes first" : "Mark ready to sync to accounting"} data-testid={`ramp-review-${tx.id}`}
                className="text-[11px] border border-brand-border rounded px-2 py-1 text-brand-muted hover:text-brand-ink disabled:opacity-30 w-full">Mark ready</button>}
        </div>
      </td>
    </tr>
  );
}

function ReceiptModal({ tx, onClose, onReplace }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await Promise.all((tx.receipt_ids || []).map((rid) => api.get(`/ramp/receipts/${rid}`).then((r) => r.data).catch(() => null)));
        if (alive) setItems(res.filter(Boolean));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [tx]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="ramp-receipt-modal">
      <div className="bg-brand-surface rounded-lg max-w-2xl w-full max-h-[88vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
          <p className="text-sm text-brand-ink flex items-center gap-2"><Paperclip className="h-4 w-4 text-brand-tertiary" /> Receipt · {tx.merchant} · {money(tx.amount, tx.currency)}</p>
          <button onClick={onClose} data-testid="ramp-receipt-close" className="text-brand-tertiary hover:text-brand-ink"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? <p className="text-sm text-brand-tertiary text-center py-10">Loading receipt…</p>
            : items.length === 0 ? <p className="text-sm text-brand-tertiary text-center py-10">Couldn't load the receipt image.</p>
            : items.map((it) => {
              const isPdf = (it.url || "").split("?")[0].toLowerCase().endsWith(".pdf");
              return isPdf
                ? <a key={it.id} href={it.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-primary underline" data-testid="ramp-receipt-pdf"><FileText className="h-4 w-4" /> Open PDF receipt</a>
                : <img key={it.id} src={it.url} alt="Receipt" className="w-full rounded border border-brand-border" data-testid="ramp-receipt-img" />;
            })}
        </div>
        <div className="flex justify-end px-5 py-3 border-t border-brand-border">
          <button onClick={() => onReplace(tx)} data-testid="ramp-receipt-replace" className="text-xs border border-brand-border rounded px-3 py-1.5 text-brand-muted hover:text-brand-ink flex items-center gap-1"><Upload className="h-3 w-3" /> Replace</button>
        </div>
      </div>
    </div>
  );
}

function SplitModal({ tx, meta, onClose, onDone }) {
  const totalCents = Math.round((tx.amount || 0) * 100);
  const [lines, setLines] = useState([
    { amount: (tx.amount || 0).toFixed(2), gl: "", loc: "", memo: "" },
    { amount: "0.00", gl: "", loc: "", memo: "" },
  ]);
  const [busy, setBusy] = useState(false);
  const glByName = useMemo(() => Object.fromEntries((meta.gl_accounts || []).map((g) => [g.name, g.external_id])), [meta]);
  const locByVal = useMemo(() => Object.fromEntries((meta.location_options || []).map((o) => [o.value, o.external_id])), [meta]);
  const sumCents = lines.reduce((a, l) => a + Math.round((parseFloat(l.amount) || 0) * 100), 0);
  const balanced = sumCents === totalCents;

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines((ls) => [...ls, { amount: "0.00", gl: "", loc: "", memo: "" }]);
  const rmLine = (i) => setLines((ls) => ls.length > 2 ? ls.filter((_, idx) => idx !== i) : ls);

  const save = async () => {
    if (!balanced) { toast.error("Split lines must add up to the charge total"); return; }
    const payload = lines.map((l) => {
      const selections = [];
      if (l.gl && meta.gl_field_external_id && glByName[l.gl]) selections.push({ field_external_id: meta.gl_field_external_id, field_option_external_id: glByName[l.gl] });
      if (l.loc && meta.location_field_external_id && locByVal[l.loc]) selections.push({ field_external_id: meta.location_field_external_id, field_option_external_id: locByVal[l.loc] });
      return { amount: Math.round((parseFloat(l.amount) || 0) * 100), memo: l.memo || null, selections };
    });
    setBusy(true);
    try { await api.patch(`/ramp/transactions/${tx.id}/split`, payload); toast.success("Charge split in Ramp"); onDone(); onClose(); }
    catch (err) { toast.error("Split failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="ramp-split-modal">
      <div className="bg-brand-surface rounded-lg max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
          <p className="text-sm text-brand-ink flex items-center gap-2"><Scissors className="h-4 w-4 text-brand-tertiary" /> Split charge · {tx.merchant} · {money(tx.amount, tx.currency)}</p>
          <button onClick={onClose} data-testid="ramp-split-close" className="text-brand-tertiary hover:text-brand-ink"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">
          <p className="text-[11px] text-brand-muted mb-3">Divide this charge into lines — each with its own amount, category, location and memo. Lines must add up to the total.</p>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`ramp-split-line-${i}`}>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-tertiary text-xs">$</span>
                  <input value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value)} data-testid={`ramp-split-amount-${i}`}
                    className="w-24 border border-brand-border rounded pl-5 pr-2 py-1.5 text-sm bg-brand-page text-brand-ink font-mono" inputMode="decimal" />
                </div>
                <select value={l.gl} onChange={(e) => setLine(i, "gl", e.target.value)} data-testid={`ramp-split-gl-${i}`} className="border border-brand-border rounded px-2 py-1.5 text-xs bg-brand-page text-brand-ink max-w-[160px]">
                  <option value="">— category —</option>
                  {(meta.gl_accounts || []).map((g) => <option key={g.external_id} value={g.name}>{g.name}</option>)}
                </select>
                <select value={l.loc} onChange={(e) => setLine(i, "loc", e.target.value)} data-testid={`ramp-split-loc-${i}`} className="border border-brand-border rounded px-2 py-1.5 text-xs bg-brand-page text-brand-ink max-w-[130px]">
                  <option value="">— location —</option>
                  {(meta.location_options || []).map((o) => <option key={o.external_id} value={o.value}>{o.value}</option>)}
                </select>
                <input value={l.memo} onChange={(e) => setLine(i, "memo", e.target.value)} placeholder="memo" data-testid={`ramp-split-memo-${i}`} className="flex-1 min-w-[100px] border border-brand-border rounded px-2 py-1.5 text-xs bg-brand-page text-brand-ink" />
                <button onClick={() => rmLine(i)} disabled={lines.length <= 2} data-testid={`ramp-split-remove-${i}`} className="text-brand-tertiary hover:text-brand-neg disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <button onClick={addLine} data-testid="ramp-split-add" className="mt-3 text-xs text-brand-primary flex items-center gap-1"><Plus className="h-3 w-3" /> Add line</button>
          <div className={`mt-4 flex items-center justify-between text-sm ${balanced ? "text-brand-pos" : "text-brand-neg"}`} data-testid="ramp-split-balance">
            <span>Allocated {money(sumCents / 100)} of {money(totalCents / 100)}</span>
            <span>{balanced ? "Balanced ✓" : `Off by ${money((sumCents - totalCents) / 100)}`}</span>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-brand-border">
          <button onClick={onClose} className="text-sm px-3 py-2 text-brand-muted">Cancel</button>
          <button onClick={save} disabled={!balanced || busy} data-testid="ramp-split-save" className="text-sm bg-brand-primary text-brand-page px-4 py-2 rounded disabled:opacity-40">{busy ? "Saving…" : "Split in Ramp"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Ramp() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [meta, setMeta] = useState({ gl_accounts: [], location_options: [], bond_locations: [], configured: true });
  const [location, setLocation] = useState(() => new URLSearchParams(window.location.search).get("location") || "all");
  const [start, setStart] = useState(daysAgoISO(30));
  const [end, setEnd] = useState(todayISO());
  const [needsCoding, setNeedsCoding] = useState(false);
  const [cardholder, setCardholder] = useState("all");
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptFor, setReceiptFor] = useState(null);
  const [splitFor, setSplitFor] = useState(null);
  const replaceRef = useRef(null);
  const [replaceTx, setReplaceTx] = useState(null);

  const loadMeta = useCallback(async () => {
    try { const { data } = await api.get("/ramp/meta"); setMeta(data); } catch { /* ignore */ }
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/ramp/transactions", { params: { location, start, end } }); setTxs(data.transactions || []); }
    catch (e) { setTxs([]); toast.error("Could not load Ramp charges", { description: e.userMessage }); } finally { setLoading(false); }
  }, [location, start, end]);
  const patchTx = useCallback((id, patch) => setTxs((cur) => cur.map((t) => t.id === id ? { ...t, ...patch } : t)), []);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { load(); }, [load]);

  const cardholders = useMemo(() => Array.from(new Set(txs.map((t) => t.cardholder).filter(Boolean))).sort(), [txs]);
  const shown = useMemo(() => {
    let list = needsCoding ? txs.filter((t) => !t.gl_category || !t.ramp_location) : txs;
    if (cardholder !== "all") list = list.filter((t) => t.cardholder === cardholder);
    return list;
  }, [txs, needsCoding, cardholder]);
  const total = useMemo(() => shown.reduce((a, t) => a + (t.amount || 0), 0), [shown]);
  const uncoded = useMemo(() => txs.filter((t) => !t.gl_category || !t.ramp_location).length, [txs]);

  if (meta.configured === false) {
    return (
      <div className="min-h-screen bg-brand-page"><Header />
        <main className="max-w-[900px] mx-auto px-6 py-16 text-center">
          <CreditCard className="h-8 w-8 text-brand-tertiary mx-auto" />
          <p className="text-brand-ink mt-3">Ramp isn't connected yet</p>
          <p className="text-sm text-brand-muted mt-1">An admin needs to add the Ramp API credentials in the backend settings.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Operations</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Card Charges</h1>
            <p className="text-sm text-brand-muted mt-1">Ramp charges by location — categorize, memo, attach receipts and mark ready, synced straight back to Ramp.</p>
          </div>
          <button onClick={load} data-testid="ramp-refresh" className="inline-flex items-center gap-1.5 text-sm border border-brand-border rounded-md px-3 py-2 bg-brand-surface text-brand-muted hover:text-brand-ink"><RotateCcw className="h-4 w-4" /> Refresh</button>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="ramp-location-filter"
            className="border border-brand-border rounded-md px-3 py-2 text-sm bg-brand-surface text-brand-ink">
            <option value="all">All locations</option>
            {(meta.bond_locations || []).map((l) => <option key={l} value={l}>{l}</option>)}
            <option value="__unassigned__">Unassigned (no card map)</option>
          </select>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="ramp-start" className="border border-brand-border rounded-md px-3 py-2 text-sm bg-brand-surface text-brand-ink" />
          <span className="text-brand-tertiary text-sm">→</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="ramp-end" className="border border-brand-border rounded-md px-3 py-2 text-sm bg-brand-surface text-brand-ink" />
          <select value={cardholder} onChange={(e) => setCardholder(e.target.value)} data-testid="ramp-cardholder-filter"
            className="border border-brand-border rounded-md px-3 py-2 text-sm bg-brand-surface text-brand-ink max-w-[200px]">
            <option value="all">All cardholders</option>
            {cardholders.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-brand-muted cursor-pointer" data-testid="ramp-needs-coding">
            <input type="checkbox" checked={needsCoding} onChange={(e) => setNeedsCoding(e.target.checked)} /> Needs coding only {uncoded ? `(${uncoded})` : ""}
          </label>
          <span className="ml-auto text-sm text-brand-muted">{shown.length} charges · <span className="font-mono text-brand-ink">{money(total)}</span></span>
        </div>

        {isAdmin && <div className="mt-4"><CardMapping bondLocations={meta.bond_locations || []} onChanged={load} /></div>}

        <div className="mt-4 bg-brand-surface border border-brand-border rounded-md overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
          ) : shown.length === 0 ? (
            <p className="py-16 text-center text-sm text-brand-tertiary" data-testid="ramp-empty">No charges for this period{location !== "all" ? ` at ${location === "__unassigned__" ? "unmapped cards" : location}` : ""}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-brand-tertiary border-b border-brand-border">
                  <th className="py-2 pr-2 pl-4">Date</th><th className="py-2 pr-2">Merchant / cardholder</th><th className="py-2 pr-2 text-right">Amount</th>
                  <th className="py-2 pr-2">Category (GL)</th><th className="py-2 pr-2">Location</th><th className="py-2 pr-2">Memo</th><th className="py-2 pr-2 text-center">Receipt</th><th className="py-2 text-center pr-4">Save / Sync</th>
                </tr></thead>
                <tbody className="[&_td:first-child]:pl-4 [&_td:last-child]:pr-4">
                  {shown.map((t) => <Row key={t.id} tx={t} meta={meta} onWrite={load} onPatch={patchTx} onReceipt={setReceiptFor} onSplit={setSplitFor} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {receiptFor && <ReceiptModal tx={receiptFor} onClose={() => setReceiptFor(null)}
        onReplace={(tx) => { setReceiptFor(null); setReplaceTx(tx); setTimeout(() => replaceRef.current?.click(), 50); }} />}
      {splitFor && <SplitModal tx={splitFor} meta={meta} onClose={() => setSplitFor(null)} onDone={load} />}
      <input ref={replaceRef} type="file" className="hidden" accept="image/*,application/pdf" data-testid="ramp-receipt-replace-input"
        onChange={async (e) => {
          const f = e.target.files?.[0]; const tx = replaceTx;
          if (!f || !tx) return;
          if (!tx.user_id) { toast.error("No cardholder on this charge"); return; }
          const fd = new FormData(); fd.append("file", f); fd.append("user_id", tx.user_id);
          try { await api.post(`/ramp/transactions/${tx.id}/receipt`, fd); toast.success("Receipt replaced in Ramp"); load(); }
          catch (err) { toast.error("Upload failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
          finally { if (replaceRef.current) replaceRef.current.value = ""; setReplaceTx(null); }
        }} />
    </div>
  );
}
